// React & Libraries
import { FunctionComponent, useEffect, useRef, useState } from "react"
import axios from "axios"

// Services & Utilities
import VIS from "../../VehicleIncidentsService"
import approvalService from "src/components/approvals/ApprovalService"
import AxiosConfig from "src/utilities/AxiosConfig"

// Components
import VideoPlayer from "src/components/video_player/VideoPlayer"
import VehicleIncidentsTableBodyTDIconBubble from "../VehicleIncidentsTable/VehicleIncidentsTableBody/VehicleIncidentsTableBodyTD/VehicleIncidentsTableBodyTDIconBubble"
import LoadingCard from "../../../../components/common/Loaders/LoadingCard/LoadingCard"
import snackBar from "src/utilities/snackBar/SnackBar"

// Styles
import "./VehicleIncidentsVideoPlayer.css"

interface Props {
  id: number
}

const VehicleIncidentsVideoPlayer: FunctionComponent<Props> = ({ id }) => {
  const { retrieveEventFromMap, setOpenVideo } = VIS
  const { retrieveEmployeeFromMap } = approvalService

  // State: Stores video URLs for inward and forward directions
  const [videoUrls, setVideoUrls] = useState<[string | null, string | null]>([null, null])

  // State: Tracks loading states for both videos
  const [loadingStates, setLoadingStates] = useState<[boolean, boolean]>([true, true])

  //State: Tracks if the video is ready to be loaded
  const [isReady, setIsReady] = useState<[boolean, boolean]>([false, false])

  // Prevents redundant fetches
  const busyRef = useRef(false)

  // Lookup event and employee info
  const eventItem = retrieveEventFromMap(id)
  const employee = retrieveEmployeeFromMap(eventItem?.EmployeeID ?? -1)

  useEffect(() => {
    if (!id || busyRef.current) return
    busyRef.current = true

    let isCancelled = false
    const blobUrls: [string | null, string | null] = [null, null]

    // Helper: Fetch video blob for given direction
    const fetchOne = async (
      direction: "forward" | "inward",
      index: number
    ): Promise<void> => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/samsara/video-stream`,
          {
            ...AxiosConfig.configValue(),
            params: { objectId: id, direction },
            responseType: "blob"
          }
        )
        if (!isCancelled) {
          const url = URL.createObjectURL(res.data)
          blobUrls[index] = url
          setVideoUrls(prev => {
            const updated = [...prev]
            updated[index] = url
            return updated as [string | null, string | null]
          })
        }
      } catch (err) {
        console.error(err)
        snackBar({
          type: "error",
          text: `Failed to load ${direction} video.`,
          timeout: 4000
        })
        // if one video doesnt load, still load and play the other
        setIsReady(prev => {
          const updated = [...prev]
          updated[index] = true
          return updated as [boolean, boolean]
        })
      } finally {
        if (!isCancelled) {
          setLoadingStates(prev => {
            const updated = [...prev]
            updated[index] = false
            return updated as [boolean, boolean]
          })
        }
      }
    }
    // Kick off both video fetches
    fetchOne("inward", 0)
    fetchOne("forward", 1)

    // Cleanup: Revoke blob URLs if component unmounts
    return () => {
      isCancelled = true
      blobUrls.forEach(url => url && URL.revokeObjectURL(url))
    }
  }, [id])

  // Render videos only when both are ready to play
  const renderVideoItem = (label: string, index: number) => {
    const url = videoUrls[index]
    const bothReady = isReady[0] && isReady[1]

      if (!url && !loadingStates[index]) {
        return (
          <div className="VehicleIncidentsVideoPlayerBodyVideoPlaceholder VehicleIncidentsVideoPlayerBodyVideoPlaceholderError">
            <p>{label} video not available</p>
            <div className="VehicleIncidentsVideoPlayerBodyVideoPlaceholderErrorEmoji">{`(>_<)`}</div>
          </div>
        )
    }

    return (
      <div>
        {!bothReady && (
          <div className="VehicleIncidentsVideoPlayerBodyVideoPlaceholder">
            <p>Loading {label.toLowerCase()} video...</p>
            <LoadingCard
              backgroundColor="transparent"
            gradient="linear-gradient(to right,rgb(15, 15, 15) 80%, #3a3a3a 90%, #2c2c2c 80%)"
            />
          </div>
        )}

        {url && !loadingStates[index] && (
          <div style={{ display: bothReady ? "block" : "none", height: "100%" }}>
            <VideoPlayer
              src={url}
              autoPlay
              controls
              onLoadedData={() => {
                setIsReady(prev => {
                  const updated = [...prev]
                  updated[index] = true
                  return updated as [boolean, boolean]
                })
              }}
            />
          </div>
        )}
      </div>
    )
  }


  return (
    <div className="VehicleIncidentsVideoPlayer">
      {/* === Header Section === */}
      <div className="VehicleIncidentsVideoPlayerHeader">
        <div className="VehicleIncidentsVideoPlayerHeaderLeft">
          <div className="VehicleIncidentsVideoPlayerHeaderItem VehicleIncidentsVideoPlayerHeaderItemEventName">
            <h6>
              <strong>Type:</strong> {eventItem?.Name}{" "}
              <VehicleIncidentsTableBodyTDIconBubble label={(eventItem?.Label ?? "").split(",").filter(Boolean)} />
            </h6>
          </div>
          <div className="VehicleIncidentsVideoPlayerHeaderItem">
            <h6><strong>Name:</strong> {`${employee?.FirstName}, ${employee?.LastName}`}</h6>
          </div>
          <div className="VehicleIncidentsVideoPlayerHeaderItem">
            <h6><strong>ID:</strong>{`${id}`}</h6>
          </div>
        </div>
        <div className="VehicleIncidentsVideoPlayerHeaderLeft">
          <div className="VehicleIncidentsVideoPlayerCloseBtn" onClick={() => setOpenVideo(-1)}>
            <span className="material-icons">close</span>
          </div>
        </div>
      </div>

      {/* === Video Display Section === */}
      <div className="VehicleIncidentsVideoPlayerBody">
        {["Inward", "Forward"].map((label, i) => (
          <div key={label} className={`VehicleIncidentsVideoPlayerVideoItem VehicleIncidentsVideoPlayerVideoItem${i}`}>
            <h6>{label}</h6>
            {renderVideoItem(label, i)}
          </div>
        ))}
      </div>
    </div>
  )
}

export default VehicleIncidentsVideoPlayer
